import { useState, useCallback } from 'react';
import { usePrefixedId } from './usePrefixedId';

export type UseDisclosureProps = {
  /**
   * If `true`, the disclosure will be disabled. The `isOpen` state will depend on
   * the `defaultIsOpen` prop.
   */
  disabled?: boolean;
  /**
   * If `true`, the disclosure will be uncontrolled and initially open.
   */
  defaultIsOpen?: boolean;
  /**
   * If `true`, allows the disclosure to be controlled. Must be used in
   * conjunction with `onOpen` and `onClose` callback props.
   */
  isOpen?: boolean;
  /**
   * Function to be called when the disclosure is opened.
   */
  onOpen?: () => void;
  /**
   * Function to be called when the disclosure is closed.
   */
  onClose?: () => void;
};

export type UseDisclosureReturn = {
  /**
   * Disclosure state.
   */
  isOpen: boolean;
  /**
   * Aria attributes to be spread on the element that triggers the disclosure.
   */
  triggerElementAriaAttributes: {
    'aria-expanded': boolean;
    'aria-controls': string;
  };
  /**
   * Function to toggle the value of `isOpen`.
   */
  onToggle: () => void;
  /**
   * Function to set `isOpen` to `true`.
   */
  onOpen: () => void;
  /**
   * Function to set `isOpen` to `false`.
   */
  onClose: () => void;
};

/**
 * Unstyled disclosure primitive for controlling the visibility of content.
 * Can be used to build components following the WAIA-ARIA Disclosure pattern.
 * @link https://www.w3.org/WAI/ARIA/apg/patterns/disclosure/
 */
export function useDisclosure({
  defaultIsOpen = false,
  disabled = false,
  isOpen: controlledIsOpen,
  onOpen,
  onClose,
}: UseDisclosureProps): UseDisclosureReturn {
  const [uncontrolledIsOpen, setUncontrolledIsOpen] = useState(defaultIsOpen);
  const isControlled = controlledIsOpen !== undefined;
  const isOpen = isControlled ? controlledIsOpen : uncontrolledIsOpen;
  const triggerElementAriaAttributes = {
    'aria-expanded': isOpen,
    'aria-controls': usePrefixedId('trellis-foundations-disclosure'),
  };

  const handleToggle = useCallback(() => {
    const newIsOpen = !isOpen;

    if (!isControlled) {
      setUncontrolledIsOpen(newIsOpen);
    }

    if (newIsOpen) {
      if (onOpen) onOpen();
    } else {
      if (onClose) onClose();
    }
  }, [isOpen, isControlled, onOpen, onClose]);

  const handleOpen = useCallback(() => {
    if (!isOpen) {
      handleToggle();
    }
  }, [isOpen, handleToggle]);

  const handleClose = useCallback(() => {
    if (isOpen) {
      handleToggle();
    }
  }, [isOpen, handleToggle]);

  return {
    isOpen: disabled ? defaultIsOpen : isOpen,
    triggerElementAriaAttributes,
    onToggle: handleToggle,
    onOpen: handleOpen,
    onClose: handleClose,
  };
}
